<template>
  <div class="coupon-usage-report">
    <!-- Filters -->
    <b-card class="mb-4">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fas fa-filter mr-2"></i>Filters
        </h5>
        <div class="">
            <b-button variant="outline-primary" class="mr-2" @click="exportReport('csv')" :disabled="loading">
                <i class="fas fa-download"></i> Export CSV
            </b-button>
            <b-button variant="outline-secondary" @click="exportReport('pdf')" :disabled="loading">
                <i class="fas fa-file-pdf"></i> Export PDF
            </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-form @submit.prevent="fetchReport">
          <b-row>
            <b-col md="3">
              <b-form-group label="Date From:">
                <b-form-datepicker v-model="filters.date_from" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Date To:">
                <b-form-datepicker v-model="filters.date_to" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hotel:">
                <b-form-select
                  v-model="filters.hotel_id"
                  :options="hotelOptions"
                  :disabled="loadingOptions"
                >
                  <template #first>
                    <b-form-select-option value="">
                      {{ loadingOptions ? 'Loading...' : 'All Hotels' }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Coupon Code:">
                <b-form-input
                  v-model="filters.coupon_code"
                  placeholder="Search coupon code..."
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="3">
              <b-form-group label="Per Page:">
                <b-form-select
                  v-model="filters.per_page"
                  :options="perPageOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col class="d-flex align-items-end">
              <b-button type="submit" variant="primary" :disabled="loading" class="mr-2">
                <i class="fas fa-search mr-2"></i>Apply Filters
              </b-button>
              <b-button variant="outline-secondary" @click="resetFilters" :disabled="loading">
                <i class="fas fa-times mr-2"></i>Clear
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </b-card>

    <!-- Results -->
    <b-card>
      <b-card-header>
        <h5 class="mb-0">
          <i class="fas fa-table mr-2"></i>
          Coupon Usage Results
          <span v-if="reportData.total" class="text-muted">
            ({{ reportData.total }} records)
          </span>
        </h5>
      </b-card-header>
      <b-card-body class="p-0">
        <div v-if="loading" class="text-center p-4">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2 mb-0">Loading coupon usage data...</p>
        </div>

        <b-table
          v-else
          :items="reportData.data"
          :fields="tableFields"
          striped
          hover
          responsive
          show-empty
          empty-text="No coupon usage records found"
        >
          <template #cell(booking_id)="data">
            <strong class="text-primary">#{{ data.item.booking_id }}</strong>
          </template>

          <template #cell(coupon_code)="data">
            <b-badge  class="p-2 bg-success">{{ data.item.coupon_code }}</b-badge>
          </template>

          <template #cell(coupon_type)="data">
            <b-badge :class="getCouponTypeVariant(data.item.coupon_type)">
              {{ data.item.coupon_type }}
            </b-badge>
          </template>

          <template #cell(coupon_value)="data">
            <span v-if="data.item.coupon_type === 'percentage'">
              {{ data.item.coupon_value }}%
            </span>
            <span v-else>
              ${{ Number(data.item.coupon_value).toFixed(2) }}
            </span>
          </template>

          <template #cell(discount_applied)="data">
            <strong class="text-success">${{ Number(data.item.discount_applied).toFixed(2) }}</strong>
          </template>

          <template #cell(total_amount)="data">
            <strong>${{ Number(data.item.total_amount).toFixed(2) }}</strong>
          </template>

          <template #cell(booking_date)="data">
            {{ formatDate(data.item.booking_date) }}
          </template>
        </b-table>

        <!-- Pagination -->
          <b-pagination
            v-if="reportData.total > 0"
            v-model="currentPage"
            :total-rows="reportData.total"
            :per-page="parseInt(filters.per_page)"
            @change="handlePageChange"
            :disabled="loading"
          />
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import { request } from '@/Util/Request';

export default {
  name: 'CouponUsageReport',
  data() {
    return {
      loading: false,
      loadingOptions: false,
      currentPage: 1,
      filters: {
        date_from: '',
        date_to: '',
        hotel_id: '',
        coupon_code: '',
        per_page: 15
      },
      reportData: {
        data: [],
        total: 0,
        current_page: 1,
        last_page: 1
      },
      perPageOptions: [
        { value: 10, text: '10 per page' },
        { value: 15, text: '15 per page' },
        { value: 25, text: '25 per page' },
        { value: 50, text: '50 per page' }
      ],
      tableFields: [
        { key: 'booking_id', label: 'Booking ID', sortable: true },
        { key: 'coupon_code', label: 'Coupon Code' },
        { key: 'user_name', label: 'Customer Name' },
        { key: 'user_email', label: 'Email' },
        { key: 'hotel_name', label: 'Hotel' },
        { key: 'coupon_type', label: 'Type' },
        { key: 'coupon_value', label: 'Value' },
        { key: 'discount_applied', label: 'Discount Applied', sortable: true },
        { key: 'total_amount', label: 'Total Amount', sortable: true },
        { key: 'booking_date', label: 'Booking Date', sortable: true }
      ]
    };
  },
  props: {
    hotelOptions:{
      type: Array,
      required:true
    }
  },
  async mounted() {
    await this.fetchReport();
  },
  methods: {
    async fetchReport() {
      try {
        this.loading = true;
        const params = {
          ...this.filters,
          page: this.currentPage
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await request({
          method: 'get',
          url: '/hotel-reports/coupon-usage',
          params
        });

        this.reportData = response;
      } catch (error) {
        console.error('Error fetching coupon usage report:', error);
        this.$bvToast.toast('Failed to load coupon usage report', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchReport();
    },

    resetFilters() {
      this.filters = {
        date_from: '',
        date_to: '',
        hotel_id: '',
        coupon_code: '',
        per_page: 15
      };
      this.currentPage = 1;
      this.fetchReport();
    },

    getCouponTypeVariant(type) {
      switch (type) {
        case '%': return 'bg-primary text-white';
        case '$': return 'bg-warning text-dark';
        default: return 'bg-secondary text-white';
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    async exportReport(format) {
      try {
        this.loading = true;
        const params = {
          ...this.filters, // or whatever you use for filter state
          format
        };
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) delete params[key];
        });

        const response = await request({
          method: 'get',
          url: '/hotel-reports/coupon-usage-export',
          params,
          responseType: 'blob'
        });

        const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
        const blob = new Blob([response], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `coupon_usage_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$bvToast.toast(`Report exported as ${format.toUpperCase()}`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast('Export failed', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* .coupon-usage-report {
  padding: 20px;
} */

.table th {
  background-color: #f8f9fa;
  border-top: none;
}

.badge {
  font-size: 0.85em;
}
</style>
